import { Controller } from "@hotwired/stimulus"

// Presents the one-time Feature Update modal on the next native app open via
// Turbo.visit (the path config presents the URL as a modal sheet). The server
// renders this controller (hidden) only for an eligible, unseen update.
//
// Hotwire Native Android loads every bottom-tab WebView at launch, each its own JS
// context, and the trigger lives in the shared layout, so each tab would fire the
// modal ("multiple in quick succession"). Per-context guards can't dedupe across
// separate WebViews, but localStorage IS shared app-wide (same origin), so we claim
// the presentation there. The claim has a short time window so the launch render
// storm collapses to one modal. Every trigger removes itself once the update is
// presented or once another WebView claims it, so native back navigation cannot
// restore a stale trigger from the page stack. The claim still expires so
// `feature_updates:reset` plus a cold relaunch/fresh render can show it again while
// testing.
const CLAIM_KEY = "featureUpdatePresentedAt"
const CLAIM_WINDOW_MS = 10000

export default class extends Controller {
  static values = { key: String, url: String }

  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    this.removeOnClaim = (event) => this.removeIfClaimed(event)
    window.addEventListener("storage", this.removeOnClaim)
    // Jitter so near-simultaneous tab loads don't all write at the same time.
    this.timeout = setTimeout(() => this.maybePresent(), 300 + Math.floor(Math.random() * 250))
  }

  disconnect() {
    clearTimeout(this.timeout)
    if (this.removeOnClaim) window.removeEventListener("storage", this.removeOnClaim)
  }

  maybePresent() {
    if (this.recentlyClaimed) {
      this.removeTrigger()
      return
    }

    const claim = this.claim()
    if (claim) {
      this.timeout = setTimeout(() => this.presentIfClaimHeld(claim), 75)
      return
    }

    this.removeTrigger()
    window.Turbo.visit(this.urlValue)
  }

  presentIfClaimHeld(claim) {
    if (!this.claimHeld(claim)) {
      this.removeTrigger()
      return
    }

    this.removeTrigger()
    window.Turbo.visit(this.urlValue)
  }

  removeIfClaimed(event) {
    if (event.key !== this.claimKey) return
    if (event.newValue === null) return

    this.removeTrigger()
  }

  removeTrigger() {
    if (!this.element.isConnected) return

    clearTimeout(this.timeout)
    if (this.removeOnClaim) window.removeEventListener("storage", this.removeOnClaim)
    this.element.remove()
  }

  claim() {
    const claim = `${Date.now()}:${Math.random().toString(36).slice(2)}`

    try {
      localStorage.setItem(this.claimKey, claim)
      return claim
    } catch {
      // localStorage unavailable (private mode / disabled): present anyway.
      return null
    }
  }

  claimHeld(claim) {
    try {
      return localStorage.getItem(this.claimKey) === claim
    } catch {
      return false
    }
  }

  get recentlyClaimed() {
    try {
      const last = parseInt(localStorage.getItem(this.claimKey) || "0", 10)
      return Date.now() - last < CLAIM_WINDOW_MS
    } catch {
      return false
    }
  }

  get claimKey() {
    return `${CLAIM_KEY}:${this.updateKey}`
  }

  get updateKey() {
    return this.hasKeyValue ? this.keyValue : this.urlValue
  }
}
